import React, { useState, useEffect, useRef, useCallback } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Link as LinkIcon,
  Visibility as VisibilityIcon,
  Info as InfoIcon,
  WorkOutline as WorkIcon,
  Cake as CakeIcon
} from '@mui/icons-material';
import CharacterSheetModal from './CharacterSheetModal';
import RelationshipGraph from './CharacterRelationships';
import CharacterDetailView from './CharacterDetailView';
import { getStoryCharacters, saveStoryCharacters, uploadCharacterImage } from '../../services/storyToolsService';

const CharacterTool = ({ projectId }) => {
  const theme = useTheme();
  const [characters, setCharacters] = useState([]);
  const [openCharacterDialog, setOpenCharacterDialog] = useState(false);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [openRelationshipDialog, setOpenRelationshipDialog] = useState(false);
  const [relationships, setRelationships] = useState([]);
  const [newRelationship, setNewRelationship] = useState({
    sourceId: '',
    targetId: '',
    type: 'friend'
  });
  const [openDetailView, setOpenDetailView] = useState(false);
  const [detailCharacter, setDetailCharacter] = useState(null);
  const [showRoleDescription, setShowRoleDescription] = useState(false);
  const [showArchetypeDescription, setShowArchetypeDescription] = useState(false);
  const [showPersonalityDescription, setShowPersonalityDescription] = useState(false);
  const [showInternalConflictDescription, setShowInternalConflictDescription] = useState(false);
  const [showExternalConflictDescription, setShowExternalConflictDescription] = useState(false);

  // Character role options with descriptions
  const characterRoles = [
    { value: 'protagonist', label: 'Protagonist', description: 'The main character whose journey we follow throughout the story.' },
    { value: 'antagonist', label: 'Antagonist', description: 'The character who opposes the protagonist and creates the central conflict.' },
    { value: 'mentor', label: 'Mentor', description: 'A character who guides and provides wisdom to the protagonist.' },
    { value: 'ally', label: 'Ally', description: 'A character who supports and helps the protagonist achieve their goals.' },
    { value: 'guardian', label: 'Guardian', description: 'A character who protects the protagonist or other characters.' },
    { value: 'contagonist', label: 'Contagonist', description: 'A character who works against the protagonist but is not the main antagonist.' },
    { value: 'love-interest', label: 'Love Interest', description: 'A character who is romantically involved with the protagonist.' },
    { value: 'sidekick', label: 'Sidekick', description: 'A character who closely supports the protagonist and provides comic relief.' },
    { value: 'foil', label: 'Foil', description: 'A character who contrasts with the protagonist to highlight certain qualities.' },
    { value: 'tertiary', label: 'Tertiary Character', description: 'A minor character who appears briefly in the story.' },
    { value: 'other', label: 'Other', description: 'A role not listed above.' }
  ];

  // Character archetype options with descriptions
  const characterArchetypes = [
    { value: 'hero', label: 'Hero', description: 'A character who is brave, noble, and willing to sacrifice for the greater good.' },
    { value: 'anti-hero', label: 'Anti-Hero', description: 'A protagonist who lacks conventional heroic qualities and may act for selfish reasons.' },
    { value: 'mentor', label: 'Mentor', description: 'A wise character who provides guidance, knowledge, and support to others.' },
    { value: 'threshold-guardian', label: 'Threshold Guardian', description: 'A character who tests the protagonist before they can advance in their journey.' },
    { value: 'herald', label: 'Herald', description: 'A character who announces the need for change or delivers an important message.' },
    { value: 'shapeshifter', label: 'Shapeshifter', description: 'A character whose loyalty and appearance are constantly changing and uncertain.' },
    { value: 'shadow', label: 'Shadow', description: 'Represents the darkness within, often manifested as the villain or antagonist.' },
    { value: 'trickster', label: 'Trickster', description: 'A character who creates mischief and embodies cunning and deceit.' },
    { value: 'caregiver', label: 'Caregiver', description: 'A character who is nurturing, compassionate, and protective of others.' },
    { value: 'everyman', label: 'Everyman', description: 'A relatable character who represents ordinary people and their struggles.' },
    { value: 'ruler', label: 'Ruler', description: 'A character who seeks control and creates order from chaos.' },
    { value: 'creator', label: 'Creator', description: 'A character who is innovative, artistic, and driven to build something new.' },
    { value: 'innocent', label: 'Innocent', description: 'A pure, naive character who sees the good in everything and everyone.' },
    { value: 'sage', label: 'Sage', description: 'A character who seeks truth and wisdom above all else.' },
    { value: 'explorer', label: 'Explorer', description: 'A character who seeks discovery, freedom, and new experiences.' },
    { value: 'outlaw', label: 'Outlaw', description: 'A character who rebels against convention and breaks the rules.' },
    { value: 'other', label: 'Other', description: 'An archetype not listed above.' }
  ];

  // Myers-Briggs personality types with descriptions
  const personalityTypes = [
    { value: 'INTJ', label: 'INTJ - The Architect', description: 'Strategic, innovative, private, and independent. INTJs are driven by their own original ideas to achieve improvements.' },
    { value: 'INTP', label: 'INTP - The Logician', description: 'Analytical, objective, conceptual, and curious. INTPs are driven by the desire to find logical explanations for everything.' },
    { value: 'ENTJ', label: 'ENTJ - The Commander', description: 'Decisive, strategic, efficient, and organized. ENTJs are natural leaders who implement long-term plans and drive others toward goals.' },
    { value: 'ENTP', label: 'ENTP - The Debater', description: 'Innovative, strategic, versatile, and enthusiastic. ENTPs love intellectual challenges and see possibilities for improvement everywhere.' },
    { value: 'INFJ', label: 'INFJ - The Advocate', description: 'Idealistic, organized, insightful, and principled. INFJs want to understand the meaning of people\'s lives and help them fulfill their potential.' },
    { value: 'INFP', label: 'INFP - The Mediator', description: 'Idealistic, empathetic, creative, and individualistic. INFPs are guided by their core values and seek to help people and causes they believe in.' },
    { value: 'ENFJ', label: 'ENFJ - The Protagonist', description: 'Charismatic, empathetic, reliable, and passionate. ENFJs are natural mentors who inspire and motivate others to grow and develop.' },
    { value: 'ENFP', label: 'ENFP - The Campaigner', description: 'Enthusiastic, creative, sociable, and free-spirited. ENFPs see life as a creative adventure and inspire others with their genuine excitement.' },
    { value: 'ISTJ', label: 'ISTJ - The Logistician', description: 'Practical, fact-minded, reliable, and responsible. ISTJs are dedicated to their duties and can always be counted on to follow through.' },
    { value: 'ISFJ', label: 'ISFJ - The Defender', description: 'Supportive, observant, warm, and dedicated. ISFJs are always ready to protect and provide for their loved ones.' },
    { value: 'ESTJ', label: 'ESTJ - The Executive', description: 'Efficient, outgoing, analytical, and systematic. ESTJs excel at organizing people and processes to get things done.' },
    { value: 'ESFJ', label: 'ESFJ - The Consul', description: 'Caring, social, popular, and traditional. ESFJs are focused on creating harmony in their environment and helping those in need.' },
    { value: 'ISTP', label: 'ISTP - The Virtuoso', description: 'Practical, logical, spontaneous, and reserved. ISTPs are master troubleshooters who enjoy understanding how things work.' },
    { value: 'ISFP', label: 'ISFP - The Adventurer', description: 'Artistic, sensitive, nurturing, and spontaneous. ISFPs follow their own path and stay true to their values.' },
    { value: 'ESTP', label: 'ESTP - The Entrepreneur', description: 'Energetic, analytical, spontaneous, and adaptable. ESTPs are action-oriented problem solvers who seize opportunities.' },
    { value: 'ESFP', label: 'ESFP - The Entertainer', description: 'Spontaneous, energetic, enthusiastic, and social. ESFPs love being the center of attention and bringing joy to others.' }
  ];

  // Internal conflict types with descriptions
  const internalConflictTypes = [
    { value: 'moral-dilemma', label: 'Moral Dilemma', description: 'A conflict between two or more moral principles, where the character must choose between equally valid but competing values.' },
    { value: 'temptation', label: 'Temptation', description: 'A struggle against a desire that the character knows they should resist but finds difficult to overcome.' },
    { value: 'self-doubt', label: 'Self-Doubt', description: 'Uncertainty about one\'s abilities, decisions, or worth that prevents the character from taking action or feeling confident.' },
    { value: 'guilt', label: 'Guilt', description: 'Remorse over past actions or inactions that continues to affect the character\'s present choices and emotional state.' },
    { value: 'identity-crisis', label: 'Identity Crisis', description: 'Confusion about who the character is, what they believe in, or what their purpose is.' },
    { value: 'fear', label: 'Fear', description: 'A deep-seated fear that prevents the character from achieving their goals or living fully.' },
    { value: 'duty-vs-desire', label: 'Duty vs. Desire', description: 'A conflict between what the character wants to do and what they feel obligated to do.' },
    { value: 'change-vs-stasis', label: 'Change vs. Stasis', description: 'A struggle between the desire to change and grow versus the comfort of remaining the same.' },
    { value: 'conflicting-loyalties', label: 'Conflicting Loyalties', description: 'Being torn between loyalty to different people, groups, or ideals.' },
    { value: 'past-trauma', label: 'Past Trauma', description: 'Dealing with the psychological effects of past traumatic experiences that affect current behavior and choices.' }
  ];

  // External conflict types with descriptions
  const externalConflictTypes = [
    { value: 'character-vs-character', label: 'Character vs. Character', description: 'Direct conflict with another character who has opposing goals, values, or methods.' },
    { value: 'character-vs-society', label: 'Character vs. Society', description: 'Struggle against social norms, laws, or institutions that oppose the character\'s goals or beliefs.' },
    { value: 'character-vs-nature', label: 'Character vs. Nature', description: 'Conflict with natural forces, environments, or disasters that threaten the character\'s survival or goals.' },
    { value: 'character-vs-technology', label: 'Character vs. Technology', description: 'Struggle against technological systems, artificial intelligence, or the consequences of technological advancement.' },
    { value: 'character-vs-supernatural', label: 'Character vs. Supernatural', description: 'Conflict with supernatural entities, forces, or phenomena beyond normal human understanding.' },
    { value: 'character-vs-fate', label: 'Character vs. Fate', description: 'Fighting against a predetermined destiny or seemingly inevitable outcome.' },
    { value: 'power-struggle', label: 'Power Struggle', description: 'Competition for control, influence, or dominance over others or resources.' },
    { value: 'rivalry', label: 'Rivalry', description: 'Ongoing competition with a peer or equal who shares similar goals or desires the same thing.' },
    { value: 'persecution', label: 'Persecution', description: 'Being targeted, hunted, or oppressed by individuals or groups with greater power.' },
    { value: 'quest', label: 'Quest', description: 'Obstacles encountered while pursuing a specific goal, object, or destination.' }
  ];

  // Character strengths with descriptions
  const characterStrengths = [
    { value: 'adaptable', label: 'Adaptable', description: 'Able to adjust to new conditions and situations with ease.' },
    { value: 'ambitious', label: 'Ambitious', description: 'Having a strong desire and determination to succeed.' },
    { value: 'brave', label: 'Brave', description: 'Ready to face and endure danger or pain; showing courage.' },
    { value: 'charismatic', label: 'Charismatic', description: 'Exercising a compelling charm that inspires devotion in others.' },
    { value: 'compassionate', label: 'Compassionate', description: 'Feeling or showing sympathy and concern for others.' },
    { value: 'confident', label: 'Confident', description: 'Feeling or showing certainty about something.' },
    { value: 'creative', label: 'Creative', description: 'Having good imagination or original ideas.' },
    { value: 'decisive', label: 'Decisive', description: 'Able to make decisions quickly and effectively.' },
    { value: 'determined', label: 'Determined', description: 'Having made a firm decision and being resolved not to change it.' },
    { value: 'disciplined', label: 'Disciplined', description: 'Showing a controlled form of behavior or way of working.' },
    { value: 'empathetic', label: 'Empathetic', description: 'Able to understand and share the feelings of another.' },
    { value: 'focused', label: 'Focused', description: 'Directing a great deal of attention, interest, or activity towards a particular aim.' },
    { value: 'honest', label: 'Honest', description: 'Free of deceit and untruthfulness; sincere.' },
    { value: 'intelligent', label: 'Intelligent', description: 'Having good understanding or a high mental capacity.' },
    { value: 'intuitive', label: 'Intuitive', description: 'Using or based on what one feels to be true even without conscious reasoning.' },
    { value: 'loyal', label: 'Loyal', description: 'Giving or showing firm and constant support or allegiance to a person or institution.' },
    { value: 'observant', label: 'Observant', description: 'Quick to notice things and attentive to details.' },
    { value: 'patient', label: 'Patient', description: 'Able to accept or tolerate delays, problems, or suffering without becoming annoyed or anxious.' },
    { value: 'persistent', label: 'Persistent', description: 'Continuing firmly or obstinately in a course of action in spite of difficulty or opposition.' },
    { value: 'resourceful', label: 'Resourceful', description: 'Having the ability to find quick and clever ways to overcome difficulties.' }
  ];

  // Character weaknesses with descriptions
  const characterWeaknesses = [
    { value: 'arrogant', label: 'Arrogant', description: 'Having or revealing an exaggerated sense of one\'s own importance or abilities.' },
    { value: 'cynical', label: 'Cynical', description: 'Believing that people are motivated by self-interest; distrustful of human sincerity or integrity.' },
    { value: 'dishonest', label: 'Dishonest', description: 'Behaving or prone to behave in an untrustworthy, deceitful, or insincere way.' },
    { value: 'fearful', label: 'Fearful', description: 'Feeling afraid; showing fear or anxiety.' },
    { value: 'greedy', label: 'Greedy', description: 'Having an excessive desire to acquire or possess more than what one needs.' },
    { value: 'hesitant', label: 'Hesitant', description: 'Tentative, unsure, or slow in acting or speaking.' },
    { value: 'impatient', label: 'Impatient', description: 'Having or showing a tendency to be quickly irritated or provoked.' },
    { value: 'impulsive', label: 'Impulsive', description: 'Acting or done without forethought; spontaneous and sudden.' },
    { value: 'indecisive', label: 'Indecisive', description: 'Not able to make decisions quickly and effectively.' },
    { value: 'insecure', label: 'Insecure', description: 'Not confident or assured; uncertain and anxious.' },
    { value: 'jealous', label: 'Jealous', description: 'Feeling or showing envy of someone or their achievements and advantages.' },
    { value: 'lazy', label: 'Lazy', description: 'Unwilling to work or use energy; characterized by lack of effort or activity.' },
    { value: 'manipulative', label: 'Manipulative', description: 'Characterized by unscrupulous control of a situation or person.' },
    { value: 'narrow-minded', label: 'Narrow-minded', description: 'Not willing to listen to or tolerate other views; prejudiced.' },
    { value: 'obsessive', label: 'Obsessive', description: 'Having excessive thoughts or feelings about something or someone.' },
    { value: 'paranoid', label: 'Paranoid', description: 'Unreasonably or obsessively anxious, suspicious, or mistrustful.' },
    { value: 'perfectionist', label: 'Perfectionist', description: 'Refusing to accept any standard short of perfection.' },
    { value: 'pessimistic', label: 'Pessimistic', description: 'Tending to see the worst aspect of things or believe that the worst will happen.' },
    { value: 'reckless', label: 'Reckless', description: 'Heedless of danger or the consequences of one\'s actions; rash or impetuous.' },
    { value: 'stubborn', label: 'Stubborn', description: 'Having or showing dogged determination not to change one\'s attitude or position.' }
  ];

  const handleViewCharacterDetail = (character) => {
    setDetailCharacter(character);
    setOpenDetailView(true);
  };

  const handleCloseDetailView = () => {
    setOpenDetailView(false);
    setDetailCharacter(null);
  };

  const handleEditFromDetailView = () => {
    setSelectedCharacter(detailCharacter);
    setOpenCharacterDialog(true);
    setOpenDetailView(false);
  };

  const getRoleDescription = (roleValue) => {
    const role = characterRoles.find(r => r.value === roleValue);
    return role ? role.description : '';
  };

  const getArchetypeDescription = (archetypeValue) => {
    const archetype = characterArchetypes.find(a => a.value === archetypeValue);
    return archetype ? archetype.description : '';
  };

  const getPersonalityDescription = (personalityValue) => {
    const personality = personalityTypes.find(p => p.value === personalityValue);
    return personality ? personality.description : '';
  };

  const getInternalConflictDescription = (conflictValue) => {
    const conflict = internalConflictTypes.find(c => c.value === conflictValue);
    return conflict ? conflict.description : '';
  };

  const getExternalConflictDescription = (conflictValue) => {
    const conflict = externalConflictTypes.find(c => c.value === conflictValue);
    return conflict ? conflict.description : '';
  };

  const getStrengthDescription = (strengthValue) => {
    const strength = characterStrengths.find(s => s.value === strengthValue);
    return strength ? strength.description : '';
  };

  const getWeaknessDescription = (weaknessValue) => {
    const weakness = characterWeaknesses.find(w => w.value === weaknessValue);
    return weakness ? weakness.description : '';
  };

  const isInitialLoadRef = useRef(true);

  // Helper: strip image data from characters for localStorage caching
  // (base64 images exceed the 5-10MB localStorage quota)
  const stripImages = (chars) => chars.map(c => {
    const lean = { ...c };
    delete lean.imageUrl;
    delete lean.moodBoardImages;
    return lean;
  });

  // Save to API immediately and cache a lean copy to localStorage
  const saveToAPI = useCallback(async (chars, rels) => {
    console.log('🔵 saveToAPI called with', chars.length, 'characters and', rels.length, 'relationships');

    // Cache lean copy (no images) to localStorage for offline fallback
    try {
      localStorage.setItem(`characters-${projectId}`, JSON.stringify(stripImages(chars)));
      localStorage.setItem(`relationships-${projectId}`, JSON.stringify(rels));
      console.log('🔵 Saved to localStorage');
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }

    // Save to API immediately (no debounce) so data is persisted before user navigates away
    try {
      const result = await saveStoryCharacters(projectId, {
        characters: chars,
        relationships: rels
      });
      console.log('✅ Story characters saved to API:', result);
    } catch (error) {
      console.error('❌ Error saving to API:', error.response?.data || error.message);
    }
  }, [projectId]);

  useEffect(() => {
    // Load characters from API with localStorage fallback
    const loadCharacters = async () => {
      try {
        const response = await getStoryCharacters(projectId);
        if (response?.data?.storyCharacters) {
          const { characters: chars, relationships: rels } = response.data.storyCharacters;
          setCharacters(chars || []);
          setRelationships(rels || []);
          console.log('✅ Loaded story characters from API');
          
          // Update localStorage with API data (strip images to avoid quota errors)
          localStorage.setItem(`characters-${projectId}`, JSON.stringify(stripImages(chars || [])));
          localStorage.setItem(`relationships-${projectId}`, JSON.stringify(rels || []));
        } else {
          throw new Error('No data from API');
        }
      } catch (error) {
        console.log('🔍 No API data, checking localStorage...');
        // Fallback to localStorage
        try {
          const savedCharacters = localStorage.getItem(`characters-${projectId}`);
          if (savedCharacters) {
            const chars = JSON.parse(savedCharacters);
            setCharacters(chars);
            console.log('✅ Loaded characters from localStorage');
            
            // Migrate localStorage data to API
            const savedRelationships = localStorage.getItem(`relationships-${projectId}`);
            const rels = savedRelationships ? JSON.parse(savedRelationships) : [];
            setRelationships(rels);
            
            // Save to API for future
            setTimeout(() => {
              saveStoryCharacters(projectId, { characters: chars, relationships: rels })
                .then(() => console.log('✅ Migrated localStorage data to API'))
                .catch(err => console.log('Migration to API pending:', err.message));
            }, 2000);
          }
          
          const savedRelationships = localStorage.getItem(`relationships-${projectId}`);
          if (savedRelationships) {
            setRelationships(JSON.parse(savedRelationships));
          }
        } catch (localError) {
          console.error('Error loading from localStorage:', localError);
        }
      }
      isInitialLoadRef.current = false;
    };
    
    loadCharacters();
    
  }, [projectId]);

  const handleSaveCharacter = (character) => {
    console.log('🔵 handleSaveCharacter called with:', character);
    let updatedCharacters;
    
    if (character.id) {
      // Update existing character
      updatedCharacters = characters.map(c => 
        c.id === character.id ? character : c
      );
    } else {
      // Add new character with generated ID
      const newCharacter = {
        ...character,
        id: Date.now().toString()
      };
      updatedCharacters = [...characters, newCharacter];
      console.log('🔵 Created new character with ID:', newCharacter.id);
    }
    
    console.log('🔵 Saving characters:', updatedCharacters.length, 'characters');
    setCharacters(updatedCharacters);
    saveToAPI(updatedCharacters, relationships);
    setOpenCharacterDialog(false);
    setSelectedCharacter(null);
  };

  const handleDeleteCharacter = (id) => {
    // Remove character
    const updatedCharacters = characters.filter(c => c.id !== id);
    setCharacters(updatedCharacters);
    
    // Remove relationships involving this character
    const updatedRelationships = relationships.filter(
      r => r.sourceId !== id && r.targetId !== id
    );
    setRelationships(updatedRelationships);
    
    saveToAPI(updatedCharacters, updatedRelationships);
  };

  const handleSaveRelationship = (relationship) => {
    // Add ID if not present
    const relationshipWithId = {
      ...relationship,
      id: relationship.id || Date.now().toString()
    };
    const updatedRelationships = [...relationships, relationshipWithId];
    setRelationships(updatedRelationships);
    saveToAPI(characters, updatedRelationships);
  };

  const handleDeleteRelationship = (index) => {
    const updatedRelationships = relationships.filter((_, i) => i !== index);
    setRelationships(updatedRelationships);
    saveToAPI(characters, updatedRelationships);
  };

  const getCharacterRelationships = (characterId) => {
    return relationships.filter(
      r => r.sourceId === characterId || r.targetId === characterId
    );
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !projectId) return;
    try {
      const url = await uploadCharacterImage(projectId, file);
      setSelectedCharacter({
        ...selectedCharacter,
        imageUrl: url
      });
    } catch (error) {
      console.error('Character image upload failed:', error);
    }
  };

  const [newStrength, setNewStrength] = useState('');
  const [newWeakness, setNewWeakness] = useState('');

  // Role-based accent color helper
  const getRoleAccent = (role) => {
    if (!role) return 'primary.main';
    const key = String(role).toLowerCase();
    if (key.includes('protagonist') || key.includes('lead')) return 'primary.main';
    if (key.includes('antagonist') || key.includes('villain')) return 'error.main';
    if (key.includes('mentor')) return 'warning.main';
    if (key.includes('ally') || key.includes('support')) return 'success.main';
    if (key.includes('love')) return 'secondary.main';
    if (key.includes('rival')) return 'info.main';
    return 'primary.main';
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
            }}
          >
            <PersonIcon sx={{ color: 'white', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Characters</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>Manage your cast, relationships, and details</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={characters.length < 2 ? 'Need 2+ characters' : 'Relationship graph'} arrow>
            <span>
              <IconButton
                onClick={() => setOpenRelationshipDialog(true)}
                aria-label="Open relationship graph"
                disabled={characters.length < 2}
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                  color: theme.palette.secondary.main,
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.secondary.main, 0.12)
                  },
                  '&.Mui-disabled': {
                    color: alpha(theme.palette.text.disabled, 0.6),
                    borderColor: alpha(theme.palette.text.disabled, 0.4)
                  }
                }}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Add character" arrow>
            <span>
              <IconButton
                onClick={() => {
                  setSelectedCharacter(null);
                  setOpenCharacterDialog(true);
                }}
                aria-label="Add character"
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  color: theme.palette.primary.main,
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.15)
                  }
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Empty state */}
      {characters.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            textAlign: 'center',
            border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
            background: (theme) => theme.palette.mode === 'dark'
              ? 'linear-gradient(145deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))'
              : 'linear-gradient(145deg, #fff, #fafafa)'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>No characters yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Add your first character to begin crafting your cast and relationships.</Typography>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => { setSelectedCharacter(null); setOpenCharacterDialog(true); }}
          >
            Add Character
          </Button>
        </Paper>
      )}

      <Grid container spacing={2} sx={{ mt: characters.length === 0 ? 2 : 0 }}>
        {characters.map(character => (
          <Grid item xs={12} sm={6} md={3} lg={2} key={character.id}>
            <Card elevation={0} sx={{ 
              height: '100%', 
              minHeight: 72,
              display: 'flex', 
              flexDirection: 'column',
              position: 'relative',
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              backgroundColor: theme.palette.mode === 'dark' 
                ? alpha(theme.palette.background.paper, 0.4)
                : theme.palette.background.paper,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 2px 8px rgba(0,0,0,0.2)'
                : '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: alpha(theme.palette.divider, 0.15),
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 4px 20px rgba(0,0,0,0.3)'
                  : '0 4px 12px rgba(0,0,0,0.08)'
              },
              '& .quick-actions': { opacity: 0, transition: 'opacity 0.15s ease' },
              '&:hover .quick-actions': { opacity: 1 }
            }}>
              {/* Hover quick actions */}
              <Box className="quick-actions" sx={{ 
                position: 'absolute', 
                top: 6, 
                right: 6, 
                display: 'flex', 
                gap: 0.25,
                zIndex: 1
              }}>
                <Tooltip title="View">
                  <IconButton 
                    size="small" 
                    onClick={(e) => { e.stopPropagation(); handleViewCharacterDetail(character); }}
                    sx={{ 
                      p: 0.5,
                      color: theme.palette.mode === 'dark' ? alpha('#fff', 0.7) : alpha('#000', 0.6),
                      '&:hover': { 
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main
                      }
                    }}
                  >
                    <VisibilityIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton 
                    size="small" 
                    onClick={(e) => { e.stopPropagation(); setSelectedCharacter(character); setOpenCharacterDialog(true); }}
                    sx={{ 
                      p: 0.5,
                      color: theme.palette.mode === 'dark' ? alpha('#fff', 0.7) : alpha('#000', 0.6),
                      '&:hover': { 
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main
                      }
                    }}
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton 
                    size="small" 
                    onClick={(e) => { e.stopPropagation(); handleDeleteCharacter(character.id); }}
                    sx={{ 
                      p: 0.5,
                      color: theme.palette.mode === 'dark' ? alpha('#fff', 0.7) : alpha('#000', 0.6),
                      '&:hover': { 
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                        color: theme.palette.error.main
                      }
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'stretch', flex: 1, p: 1.5 }}>
                {/* Portrait image */}
                <Box
                  sx={{
                    width: 48,
                    height: 56,
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`
                  }}
                >
                  {character.imageUrl ? (
                    <Box
                      component="img"
                      src={character.imageUrl}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <PersonIcon sx={{ fontSize: 24, color: alpha(theme.palette.primary.main, 0.5) }} />
                  )}
                </Box>

                {/* Name and role */}
                <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', pl: 1.5 }}>
                  <Typography 
                    variant="body2" 
                    component="div" 
                    sx={{ 
                      fontWeight: 600, 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap',
                      lineHeight: 1.3
                    }}
                  >
                    {character.name || 'Untitled'}
                  </Typography>
                  {(character.role || character.archetype) && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'text.secondary',
                        fontSize: '0.7rem',
                        mt: 0.25,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {character.role || character.archetype}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Character Sheet Modal */}
      <CharacterSheetModal
        open={openCharacterDialog}
        onClose={() => setOpenCharacterDialog(false)}
        character={selectedCharacter}
        onSave={handleSaveCharacter}
        allCharacters={characters}
        projectId={projectId}
      />

      {/* Relationship Dialog */}
      <RelationshipGraph
        open={openRelationshipDialog}
        onClose={() => setOpenRelationshipDialog(false)}
        characters={characters}
        relationships={relationships}
        onSaveRelationship={handleSaveRelationship}
        onDeleteRelationship={handleDeleteRelationship}
      />

      {/* Character Detail View Dialog */}
      <CharacterDetailView
        open={openDetailView}
        onClose={handleCloseDetailView}
        character={detailCharacter}
        onEdit={handleEditFromDetailView}
        relationships={relationships}
        getCharacterNameById={(id) => {
          const character = characters.find(c => c.id === id);
          return character ? character.name : 'Unknown Character';
        }}
      />

    </Box>
  );
};

export default CharacterTool;
